import React, {useEffect, useMemo, useRef, useState} from 'react';
import API from "../utils/API";
import {API_ENDPOINTS} from "../apiConfig";
import Forms from "./Forms";
import Error from "./Error";
import {useModal} from "../contexts/ModalContext";
import PartVariantModal from "./PartVariantModal";
import EditIconButton from "./Forms/EditIconButton";
import DeleteIconButton from "./Forms/DeleteIconButton";

const TYPE_OPTIONS = [
    {value: 'sparePart', label: 'Náhradní díl'},
    {value: 'product', label: 'Produkt'}
];

const emptyField = () => ({
    parameter: null,
    required: false,
    sortOrder: 0,
    active: true
});

const slugifyCode = (value) => String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);

const PartMasterModal = ({close, onSaved, part}) => {
    const inputRef = useRef(null);
    const {openModal, confirm} = useModal();
    const [options, setOptions] = useState({categories: [], devices: [], parameters: []});
    const [activeTab, setActiveTab] = useState('master');
    const [variants, setVariants] = useState([]);
    const [loadingVariants, setLoadingVariants] = useState(false);
    const [form, setForm] = useState({
        name: '',
        code: '',
        description: '',
        type: TYPE_OPTIONS[0],
        categoryList: [],
        compatibleWith: [],
        variantSchema: []
    });
    const [error, setError] = useState(null);
    const isEdit = !!part?._id;

    useEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 1);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const [catRes, devRes, paramRes] = await Promise.all([
                    API.get(API_ENDPOINTS.part.categories),
                    API.get(API_ENDPOINTS.device.list),
                    API.get(API_ENDPOINTS.partParameter.list)
                ]);
                setOptions({
                    categories: (catRes.data || []).map((cat) => ({value: cat._id, label: cat.name, code: cat.code})),
                    devices: (devRes.data || []).map((dev) => ({value: dev._id, label: dev.name})),
                    parameters: (paramRes.data || []).map((item) => ({
                        value: item._id,
                        label: `${item.label} (${item.key})`,
                        paramLabel: item.label,
                        key: item.key,
                        type: item.type
                    }))
                });
            } catch (e) {
                setError(e);
            }
        })();
    }, []);

    useEffect(() => {
        if (!part) return;
        setForm({
            name: part.name || '',
            code: part.code || '',
            description: part.description || '',
            type: TYPE_OPTIONS.find((x) => x.value === part.type) || TYPE_OPTIONS[0],
            categoryList: (part.categoryList || []).map((cat) => ({value: cat._id, label: cat.name})),
            compatibleWith: (part.compatibleWith || []).map((dev) => ({value: dev._id, label: dev.name})),
            variantSchema: (part.variantSchema || []).map((it) => ({
                parameter: it?.parameter?._id || it?.parameter || null,
                legacyKey: it?.key || '',
                required: !!it.required,
                sortOrder: Number(it.sortOrder) || 0,
                active: it.active !== false
            }))
        });
    }, [part]);

    useEffect(() => {
        if (!options.parameters?.length) return;
        setForm((prev) => ({
            ...prev,
            variantSchema: (prev.variantSchema || []).map((row) => {
                if (row.parameter || !row.legacyKey) return row;
                const found = options.parameters.find((opt) => String(opt.key || '') === String(row.legacyKey || ''));
                return found ? {...row, parameter: found.value} : row;
            })
        }));
    }, [options.parameters]);

    const loadVariants = async (masterId) => {
        if (!masterId) return;
        setLoadingVariants(true);
        try {
            const {data} = await API.get(API_ENDPOINTS.partSku.list);
            setVariants((data || []).filter((item) => String(item?.part?._id || item?.part) === String(masterId)));
        } catch (e) {
            setError(e);
        } finally {
            setLoadingVariants(false);
        }
    };

    useEffect(() => {
        if (!isEdit) return;
        loadVariants(part._id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, part?._id]);

    const normalizedSchema = useMemo(() => (form.variantSchema || [])
        .map((it) => ({
            parameter: it.parameter,
            required: !!it.required,
            sortOrder: Number(it.sortOrder) || 0,
            active: it.active !== false
        }))
        .filter((it) => it.parameter), [form.variantSchema]);

    const attributeLabelsByKey = useMemo(() => {
        const labels = new Map();
        (options.parameters || []).forEach((parameter) => {
            if (parameter?.key) labels.set(String(parameter.key), parameter.paramLabel || parameter.label || parameter.key);
        });
        (part?.variantSchema || []).forEach((schemaRow) => {
            const parameter = schemaRow?.parameter || {};
            const key = parameter?.key || schemaRow?.key;
            const label = parameter?.label || schemaRow?.label;
            if (key && label) labels.set(String(key), label);
        });
        return labels;
    }, [options.parameters, part?.variantSchema]);

    const formatVariantAttributes = (attributes) => Object.entries(attributes || {})
        .map(([key, value]) => `${attributeLabelsByKey.get(String(key)) || key}: ${String(value)}`)
        .join(', ');

    const setSchemaRow = (index, patch) => {
        setForm((prev) => ({
            ...prev,
            variantSchema: prev.variantSchema.map((row, i) => i === index ? {...row, ...patch} : row)
        }));
    };

    const addSchemaRow = () => {
        setForm((prev) => ({...prev, variantSchema: [...prev.variantSchema, emptyField()]}));
    };

    const removeSchemaRow = (index) => {
        setForm((prev) => ({...prev, variantSchema: prev.variantSchema.filter((_, i) => i !== index)}));
    };

    const selectedParameterIds = new Set((form.variantSchema || []).map((row) => String(row.parameter || '')).filter(Boolean));

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            const payload = {
                name: form.name.trim(),
                code: form.code?.trim() ? slugifyCode(form.code) : slugifyCode(form.name),
                description: form.description || '',
                type: form.type.value,
                categoryList: form.categoryList.map((x) => x.value),
                compatibleWith: form.compatibleWith.map((x) => x.value),
                variantSchema: normalizedSchema
            };
            let masterId = part?._id;
            if (isEdit) {
                await API.put(API_ENDPOINTS.part.edit(part._id), payload);
            } else {
                const created = await API.post(API_ENDPOINTS.part.add, payload);
                masterId = created?.data?._id || null;
            }
            await onSaved?.();
            if (masterId && isEdit) {
                await loadVariants(masterId);
            } else {
                close();
            }
        } catch (err) {
            setError(err);
        }
    };

    const openCreateVariant = () => {
        const masterId = part?._id;
        if (!masterId) return;
        openModal(
            PartVariantModal,
            {onSaved: () => loadVariants(masterId), masterPartId: masterId},
            {title: 'Nová varianta'}
        );
    };

    const openEditVariant = (variant) => {
        openModal(
            PartVariantModal,
            {variant, onSaved: () => loadVariants(part?._id), masterPartId: part?._id},
            {title: 'Upravit variantu'}
        );
    };

    const removeVariant = async (variant) => {
        const ok = await confirm(`Smazat variantu ${variant.name}?`);
        if (!ok) return;
        await API.delete(API_ENDPOINTS.partSku.delete(variant._id));
        await loadVariants(part?._id);
    };

    return (
        <div>
            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden mb-4">
                <button
                    type="button"
                    className={`px-3 py-2 text-sm ${activeTab === 'master' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'}`}
                    onClick={() => setActiveTab('master')}
                >
                    Master
                </button>
                <button
                    type="button"
                    disabled={!isEdit}
                    className={`px-3 py-2 text-sm ${activeTab === 'variants' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'} ${!isEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => setActiveTab('variants')}
                >
                    Varianty
                </button>
            </div>

            {activeTab === 'master' ? (
                <form onSubmit={submit}>
                    <div className="grid grid-cols-2 gap-4">
                        <Forms.Text label="Název" value={form.name} onChange={(v) => setForm((p) => ({...p, name: v}))} ref={inputRef}/>
                        <Forms.Text label="Kód" value={form.code} onChange={(v) => setForm((p) => ({...p, code: v}))}/>
                        <Forms.Select label="Typ" value={form.type} options={TYPE_OPTIONS} onChange={(v) => setForm((p) => ({...p, type: v || TYPE_OPTIONS[0]}))}/>
                        <Forms.Text label="Popis" value={form.description} onChange={(v) => setForm((p) => ({...p, description: v}))}/>
                        <Forms.Select label="Kategorie" multiple value={form.categoryList} options={options.categories} onChange={(v) => setForm((p) => ({...p, categoryList: v || []}))}/>
                        <Forms.Select label="Kompatibilita zařízení" multiple value={form.compatibleWith} options={options.devices} onChange={(v) => setForm((p) => ({...p, compatibleWith: v || []}))}/>
                    </div>

                    <div className="mt-5 border rounded-md p-3">
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-medium">Parametry variant</div>
                            <Forms.Button type="button" label="Přidat parametr" onClick={addSchemaRow}/>
                        </div>
                        <div className="space-y-2">
                            {(form.variantSchema || []).map((row, idx) => (
                                <div key={`${idx}_${row.parameter || 'empty'}`} className="grid grid-cols-12 gap-2 items-end border rounded p-2">
                                    <div className="col-span-7">
                                        <Forms.Select
                                            label="Parametr"
                                            value={options.parameters.find((x) => String(x.value) === String(row.parameter || '')) || null}
                                            options={options.parameters.filter((option) => !selectedParameterIds.has(String(option.value)) || String(option.value) === String(row.parameter || ''))}
                                            onChange={(v) => setSchemaRow(idx, {parameter: v?.value || null})}
                                        />
                                    </div>
                                    <div className="col-span-2"><Forms.Number label="Order" value={row.sortOrder} onChange={(v) => setSchemaRow(idx, {sortOrder: Number(v) || 0})}/></div>
                                    <div className="col-span-1"><Forms.Checkbox label="Req" value={!!row.required} onChange={(v) => setSchemaRow(idx, {required: v})}/></div>
                                    <div className="col-span-1"><Forms.Checkbox label="On" value={!!row.active} onChange={(v) => setSchemaRow(idx, {active: v})}/></div>
                                    <div className="col-span-1"><Forms.Button type="button" label="X" className="bg-red-600 hover:bg-red-700" onClick={() => removeSchemaRow(idx)}/></div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {error ? <Error error={error}/> : null}
                    <div className="flex gap-3 mt-4">
                        <Forms.Button submit label={isEdit ? 'Uložit master' : 'Vytvořit master'}/>
                        <Forms.Button type="button" label="Zavřít" className="bg-gray-600 hover:bg-gray-700" onClick={close}/>
                    </div>
                </form>
            ) : (
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <div className="font-medium">Varianty pro {part?.name}</div>
                        <Forms.Button type="button" label="Přidat variantu" onClick={openCreateVariant}/>
                    </div>
                    <div className="border rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-100">
                            <tr>
                                <th className="text-left p-2">Název</th>
                                <th className="text-left p-2">SKU</th>
                                <th className="text-left p-2">Sklad</th>
                                <th className="text-left p-2">Atributy</th>
                                <th className="text-right p-2">Akce</th>
                            </tr>
                            </thead>
                            <tbody>
                            {loadingVariants ? (
                                <tr><td className="p-3" colSpan={5}>Načítám…</td></tr>
                            ) : null}
                            {!loadingVariants && !variants.length ? (
                                <tr><td className="p-3" colSpan={5}>Zatím bez variant.</td></tr>
                            ) : null}
                            {!loadingVariants && variants.map((row) => (
                                <tr key={row._id} className="border-t">
                                    <td className="p-2">{row.name}</td>
                                    <td className="p-2">{row.sku}</td>
                                    <td className="p-2">{row.stockQuantity}</td>
                                    <td className="p-2">{formatVariantAttributes(row.attributes)}</td>
                                    <td className="p-2">
                                        <div className="flex justify-end">
                                            <EditIconButton title="Upravit variantu" onClick={() => openEditVariant(row)}/>
                                            <DeleteIconButton title="Smazat variantu" onClick={() => removeVariant(row)}/>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PartMasterModal;
